import { defineStore } from 'pinia'
import { isCancel } from 'axios'
import {
    each, flatMap, isEmpty, keys, map, some,
} from 'lodash'
import dependencyStatus from '@/constants/dependencyStatus'
import { api, mock } from '@/services'

export const useWidgetsStore = defineStore('widgets', {
    state: () => ({
        dependenciesByWidgetType: {},
    }),
    getters: {
        dependency: (state) => (widgetType, dependencyUrl) => {
            return state.dependenciesByWidgetType[widgetType]?.[dependencyUrl]
        },
        dependencies: (state) => (widgetType) => {
            return state.dependenciesByWidgetType[widgetType] ?? {}
        },
        isLoading: (state) => (widgetType, dependenciesToCheck) => {
            return some(dependenciesToCheck, (dependencyToCheck) => {
                const status = state.dependenciesByWidgetType[widgetType]?.[dependencyToCheck.url]?.status
                return (status ?? dependencyStatus.pending) === dependencyStatus.pending
                    // If the items are not empty it means keepContentWhileRefreshing has been used, we don't want to show loading in that case
                    && isEmpty(state.dependenciesByWidgetType[widgetType]?.[dependencyToCheck.url]?.data?.items)
            })
        },
        items: (state) => (widgetType, dependenciesToReturn) => {
            return flatMap(dependenciesToReturn, (dependencyToReturn) => {
                const dependencyItems = state.dependenciesByWidgetType[widgetType]?.[dependencyToReturn.url]?.data?.items ?? []
                return map(dependencyItems, (item) => ({
                    dependency: dependencyToReturn,
                    ...item,
                }))
            })
        },
    },
    actions: {
        clearDependencies(widgetType) {
            delete this.dependenciesByWidgetType[widgetType]
        },
        clearAllDependencies() {
            this.dependenciesByWidgetType = {}
        },
        requestDependency(widgetType, dependency, abortController, fetchWithoutCache = false) {
            if (mock.mockingSpace() || mock.mockingNews()) {
                const mockLookupUrl = dependency.original_url ?? dependency.url
                return Promise.resolve({ data: mock.content()?.dependencies?.[widgetType]?.[mockLookupUrl] ?? [] })
            }

            return api.get(dependency.url, {
                headers: fetchWithoutCache ? { 'X-NO-CACHE': true } : {},
                signal: abortController.signal,
            })
        },
        fetchDependency(widgetType, dependency, refresh = false, keepContentWhileRefreshing = false) {
            this.dependenciesByWidgetType[widgetType] ??= {}
            const widgetDependencies = this.dependenciesByWidgetType[widgetType]
            const fetchWithoutCache = refresh || widgetDependencies[dependency.url]?.shouldRefresh

            if (fetchWithoutCache || !widgetDependencies[dependency.url] || [dependencyStatus.rejected, dependencyStatus.cancelled].includes(widgetDependencies[dependency.url].status)) {
                const controller = new AbortController()
                widgetDependencies[dependency.url] = {
                    requested: new Date(),
                    status: dependencyStatus.pending,
                    shouldRefresh: false,
                    promise: null,
                    controller,
                    error: null,
                    data: keepContentWhileRefreshing
                        ? widgetDependencies[dependency.url]?.data ?? []
                        : [],
                }

                widgetDependencies[dependency.url].promise = this.requestDependency(widgetType, dependency, controller, fetchWithoutCache)
                    .then((result) => {
                        widgetDependencies[dependency.url].status = dependencyStatus.fulfilled
                        widgetDependencies[dependency.url].data = result.data
                        return result
                    })
                    .catch((error) => {
                        if (isCancel(error)) {
                            widgetDependencies[dependency.url].status = dependencyStatus.cancelled
                            return
                        }
                        widgetDependencies[dependency.url].status = dependencyStatus.rejected
                        widgetDependencies[dependency.url].error = error
                        widgetDependencies[dependency.url].data = []
                        throw error
                    })
            }
            return widgetDependencies[dependency.url].promise
        },
        fetchDependencies(widgetType, dependencies, refresh = false) {
            return Promise.allSettled(map(dependencies, (dependency) => {
                return this.fetchDependency(widgetType, dependency, refresh)
            }))
        },
        // Mark all dependencies for the widget type if the dependencies aren't specified
        markDependenciesForRefresh(widgetType, dependencies = null) {
            const dependencyUrls = dependencies
                ? map(dependencies, (dependency) => dependency.url)
                : keys(this.dependenciesByWidgetType[widgetType] ?? {})

            each(dependencyUrls, (dependencyUrl) => {
                if (this.dependenciesByWidgetType[widgetType]?.[dependencyUrl]) {
                    this.dependenciesByWidgetType[widgetType][dependencyUrl].shouldRefresh = true
                }
            })
        },
        cancelPendingDependencies(widgetType, dependencies) {
            each(dependencies, (dependency) => {
                if (!this.dependenciesByWidgetType[widgetType]?.[dependency.url]) {
                    return
                }
                const config = this.dependenciesByWidgetType[widgetType][dependency.url]
                if (config.status !== dependencyStatus.pending) {
                    return
                }
                console.log('Cancelling request for dependency', dependency)
                config.controller.abort()
            })
        },
        async requestWidgetShow(url, refresh = false, config = {}) {
            if (mock.mockingNews()) {
                return Promise.resolve({ data: mock.content()?.show_data?.[url] ?? {} })
            }

            return api.get(url, {
                ...config,
                headers: {
                    ...config.headers,
                    ...(refresh ? { 'X-NO-CACHE': true } : {}),
                },
            })
        },
        async fetchWidgetShow(url, refresh = false, config = {}) {
            return this.requestWidgetShow(url, refresh, config)
        },
    },
})
